// SPDX-License-Identifier: AGPL-3.0-or-later

import Channels from '@app/features/channel/state/Channels';
import Guilds from '@app/features/guild/state/Guilds';
import GuildMembers from '@app/features/member/state/GuildMembers';
import type {Message as MessageModel} from '@app/features/messaging/models/MessagingMessage';
import Messages from '@app/features/messaging/state/MessagingMessages';
import {readMentionShape, resolveMentionSignals} from '@app/features/messaging/utils/MentionPredicate';
import {resolveReadStateEntryStatus} from '@app/features/read_state/state/read_states/ReadStateEntryStatusMachine';
import {resolveReadStateMention} from '@app/features/read_state/state/read_states/ReadStateMentionMachine';
import {
	compareMessageIds,
	normalizeCount,
	parseTimestamp,
	snowflakeTimestamp,
} from '@app/features/read_state/state/read_states/shared';
import Relationships from '@app/features/relationship/state/Relationships';
import ThreadMemberships from '@app/features/threads/state/ThreadMemberships';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';
import Users from '@app/features/user/state/Users';
import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import type {Message as WireMessage} from '@fluxer/schema/src/domains/message/MessageResponseSchemas';

export class ReadStateEntry {
	readonly channelId: string;
	storedGuildId: string | null = null;
	messagesLoaded = false;
	readStateKnown = false;
	private storedLastMessageId: string | null = null;
	private storedLastMessageTimestamp = 0;
	private storedAckMessageId: string | null = null;
	private storedAckMessageTimestamp = 0;
	acknowledgedPinTimestamp = 0;
	lastPinTimestamp = 0;
	ackedManually = false;
	private storedOldestUnreadMessageId: string | null = null;
	oldestUnreadNeedsRecompute = false;
	private storedStickyUnreadMessageId: string | null = null;
	estimated = false;
	private storedUnreadCount = 0;
	private storedRemainingUnread = 0;
	private remainingUnreadInitialized = false;
	private hasAuthoritativeUnreadTotal = false;
	private pendingUnreadThroughId: string | null = null;
	private pendingLiveAddsAfterThrough = 0;
	private countedAckMessageId: string | null = null;
	private countedLastMessageId: string | null = null;
	private storedMentionCount = 0;
	private storedMentionCountedThroughId: string | null = null;
	inFlightAckMessageId: string | null = null;
	serverVersion: string | null = null;
	constructor(channelId: string) {
		this.channelId = channelId;
	}

	get guildId(): string | null {
		const channel = Channels.getChannel(this.channelId);
		return channel?.guildId ?? this.storedGuildId ?? null;
	}

	get lastMessageId(): string | null {
		return this.storedLastMessageId;
	}

	set lastMessageId(messageId: string | null) {
		this.storedLastMessageId = messageId;
		this.storedLastMessageTimestamp = snowflakeTimestamp(messageId);
	}

	get ackMessageId(): string | null {
		return this.storedAckMessageId;
	}

	set ackMessageId(messageId: string | null) {
		this.storedAckMessageId = messageId;
		this.storedAckMessageTimestamp = snowflakeTimestamp(messageId);
	}

	get oldestUnreadMessageId(): string | null {
		return this.storedOldestUnreadMessageId;
	}

	set oldestUnreadMessageId(messageId: string | null) {
		this.storedOldestUnreadMessageId = messageId;
		this.oldestUnreadNeedsRecompute = false;
	}

	get stickyUnreadMessageId(): string | null {
		return this.storedStickyUnreadMessageId;
	}

	set stickyUnreadMessageId(messageId: string | null) {
		this.storedStickyUnreadMessageId = messageId;
	}

	get visualUnreadMessageId(): string | null {
		return this.storedStickyUnreadMessageId ?? this.storedOldestUnreadMessageId;
	}

	clearStickyUnread(): void {
		this.storedStickyUnreadMessageId = null;
	}

	noteIncomingUnread(): void {
		if (
			this.countedLastMessageId != null &&
			compareMessageIds(this.storedLastMessageId, this.countedLastMessageId) <= 0
		) {
			return;
		}
		if (
			this.pendingUnreadThroughId != null &&
			compareMessageIds(this.storedLastMessageId, this.pendingUnreadThroughId) > 0
		) {
			this.pendingLiveAddsAfterThrough++;
		}
		this.unreadCount = this.storedUnreadCount + 1;
		this.storedRemainingUnread = this.storedUnreadCount;
		this.remainingUnreadInitialized = true;
		this.countedLastMessageId = this.storedLastMessageId;
	}

	beginUnreadTotalRequest(throughId: string): void {
		if (this.pendingUnreadThroughId === throughId) {
			return;
		}
		this.pendingUnreadThroughId = throughId;
		this.pendingLiveAddsAfterThrough = 0;
	}

	get hasServerUnreadTotal(): boolean {
		return this.hasAuthoritativeUnreadTotal;
	}

	applyAuthoritativeUnreadTotal(count: number, afterId: string, throughId: string): void {
		let remaining = normalizeCount(count);
		if (this.storedAckMessageId != null && compareMessageIds(this.storedAckMessageId, afterId) > 0) {
			let crossed = 0;
			Messages.getMessages(this.channelId).forEachBuffered((message) => {
				if (compareMessageIds(message.id, afterId) > 0 && compareMessageIds(message.id, this.storedAckMessageId) <= 0) {
					crossed++;
				}
			});
			remaining = Math.max(0, remaining - crossed);
		}
		if (this.pendingUnreadThroughId === throughId) {
			remaining += this.pendingLiveAddsAfterThrough;
		} else if (this.storedLastMessageId != null && compareMessageIds(this.storedLastMessageId, throughId) > 0) {
			remaining += this.pendingLiveAddsAfterThrough;
		}
		this.unreadCount = remaining;
		this.storedRemainingUnread = remaining;
		this.remainingUnreadInitialized = true;
		this.hasAuthoritativeUnreadTotal = true;
		this.estimated = false;
		this.countedAckMessageId = this.storedAckMessageId;
		this.countedLastMessageId = this.storedLastMessageId;
		this.pendingUnreadThroughId = null;
		this.pendingLiveAddsAfterThrough = 0;
	}

	noteLastMessageIdAdvanceIfTracking(): void {
		if (!this.remainingUnreadInitialized && !this.hasAuthoritativeUnreadTotal) {
			return;
		}
		this.noteIncomingUnread();
	}

	get unreadCount(): number {
		return this.storedUnreadCount;
	}

	set unreadCount(count: number) {
		this.storedUnreadCount = normalizeCount(count);
	}

	get mentionCount(): number {
		return this.storedMentionCount;
	}

	set mentionCount(count: number) {
		this.storedMentionCount = normalizeCount(count);
	}

	get mentionCountedThroughId(): string | null {
		return this.storedMentionCountedThroughId;
	}

	set mentionCountedThroughId(messageId: string | null) {
		this.storedMentionCountedThroughId = messageId;
	}

	noteLocalMention(messageId: string): boolean {
		if (
			this.storedMentionCountedThroughId != null &&
			compareMessageIds(messageId, this.storedMentionCountedThroughId) <= 0
		) {
			return false;
		}
		this.storedMentionCountedThroughId = messageId;
		return true;
	}

	acceptServerMention(messageId: string): boolean {
		if (
			this.storedMentionCountedThroughId != null &&
			compareMessageIds(messageId, this.storedMentionCountedThroughId) < 0
		) {
			return false;
		}
		this.storedMentionCountedThroughId = messageId;
		return true;
	}

	get oldestUnreadMessageTimestamp(): number {
		return snowflakeTimestamp(this.oldestUnreadMessageId);
	}

	get ackTimestamp(): number {
		if (this.storedAckMessageTimestamp !== 0 && !Number.isNaN(this.storedAckMessageTimestamp)) {
			return this.storedAckMessageTimestamp;
		}
		return this.ackFloorTimestamp;
	}

	private get ackFloorTimestamp(): number {
		const guildId = this.guildId;
		if (guildId != null) {
			const joinedAt = parseTimestamp(Guilds.getGuild(guildId)?.joinedAt);
			if (joinedAt !== 0) {
				return joinedAt;
			}
		}
		return snowflakeTimestamp(this.channelId);
	}

	isNewerThanAck(messageId: string): boolean {
		if (this.storedAckMessageId != null) {
			return compareMessageIds(messageId, this.storedAckMessageId) > 0;
		}
		return snowflakeTimestamp(messageId) > this.ackTimestamp;
	}

	get isPrivate(): boolean {
		const channel = Channels.getChannel(this.channelId);
		return channel?.isPrivate() ?? false;
	}

	supportsUnreadTracking(): boolean {
		return Channels.getChannel(this.channelId) != null || this.storedGuildId != null;
	}

	private get statusModel() {
		return resolveReadStateEntryStatus({
			supportsUnreadTracking: this.supportsUnreadTracking(),
			hasBlockedDirectMessageRecipient: this.hasBlockedDirectMessageRecipient(),
			lastMessageId: this.storedLastMessageId,
			ackMessageId: this.storedAckMessageId,
			ackTimestamp: this.ackTimestamp,
			lastMessageTimestamp: this.storedLastMessageTimestamp,
			mentionCount: this.mentionCount,
		});
	}

	canBeUnread(): boolean {
		return this.statusModel.canBeUnread;
	}

	supportsMentions(): boolean {
		return this.statusModel.supportsMentions;
	}

	hasUnread(): boolean {
		return this.statusModel.hasUnread;
	}

	private hasBlockedDirectMessageRecipient(): boolean {
		const channel = Channels.getChannel(this.channelId);
		if (channel?.type !== ChannelTypes.DM) {
			return false;
		}
		const recipientId = channel.getRecipientId();
		return recipientId != null && Relationships.isBlocked(recipientId);
	}

	hasMentions(): boolean {
		return this.mentionCount > 0;
	}

	isUnreadOrMentioned(): boolean {
		return this.statusModel.isUnreadOrMentioned;
	}

	rebuild(
		ackMessageId?: string | null,
		{
			recomputeMentions = false,
		}: {
			recomputeMentions?: boolean;
		} = {},
	): void {
		const previousAckId = this.countedAckMessageId;
		const previousLastId = this.countedLastMessageId;
		if (ackMessageId !== undefined) {
			this.ackMessageId = ackMessageId;
		} else {
			this.ackMessageId = this.storedAckMessageId ?? this.guessAckMessageId();
		}
		const ackRewound =
			previousAckId != null &&
			this.storedAckMessageId != null &&
			compareMessageIds(this.storedAckMessageId, previousAckId) < 0;
		if (ackRewound) {
			this.resetRemainingUnreadTracking();
		}
		const previousRemaining = this.remainingUnreadInitialized ? this.storedRemainingUnread : this.storedUnreadCount;
		this.oldestUnreadMessageId = null;
		this.estimated = false;
		this.unreadCount = 0;
		if (recomputeMentions) {
			this.mentionCount = 0;
			this.storedMentionCountedThroughId = this.storedLastMessageId;
		}
		if (!this.hasUnread()) {
			this.resetRemainingUnreadTracking();
			return;
		}
		const currentUser = Users.getCurrentUser();
		if (currentUser == null) {
			return;
		}
		const messages = Messages.getMessages(this.channelId);
		const isPrivate = this.isPrivate;
		const userId = currentUser.id;
		const guildId = this.guildId;
		const channelId = this.channelId;
		const suppressEveryone = recomputeMentions ? UserGuildSettings.isEveryoneMentionSuppressed(guildId) : false;
		const suppressRoles = recomputeMentions ? UserGuildSettings.isRoleMentionSuppressed(guildId) : false;
		const isMuted = recomputeMentions ? UserGuildSettings.isGuildOrChannelMuted(guildId, channelId) : false;
		const member = recomputeMentions && guildId ? GuildMembers.getMember(guildId, userId) : null;
		const memberRoles = member?.roles ?? null;
		let foundAckMessage = false;
		let loadedOlderMessages = false;
		let oldestUnread: string | null = null;
		let loadedUnreadCount = 0;
		let crossedSinceLastCount = 0;
		let addedSinceLastCount = 0;
		messages.forEachBuffered((message) => {
			if (!foundAckMessage) {
				foundAckMessage = message.id === this.storedAckMessageId;
			} else if (this.storedOldestUnreadMessageId == null) {
				this.storedOldestUnreadMessageId = message.id;
			}
			if (
				previousAckId != null &&
				compareMessageIds(message.id, previousAckId) > 0 &&
				compareMessageIds(message.id, this.storedAckMessageId) <= 0
			) {
				crossedSinceLastCount++;
			}
			if (previousLastId != null && compareMessageIds(message.id, previousLastId) > 0) {
				addedSinceLastCount++;
			}
			if (this.isNewerThanAck(message.id)) {
				loadedUnreadCount++;
				if (recomputeMentions && !Relationships.isBlocked(message.author.id)) {
					const signals = resolveMentionSignals({
						...readMentionShape(message),
						currentUserId: userId,
						authorId: message.author.id,
						memberRoleIds: memberRoles,
						suppressEveryone: suppressEveryone || this.isNonMemberThread(),
						suppressRoles,
					});
					const mention = resolveReadStateMention({
						authorBlocked: false,
						hasUserMention: signals.hasUserMention,
						hasEveryoneMention: signals.hasEveryoneMention,
						hasRoleMention: signals.hasRoleMention,
						isPrivate,
						isMuted,
					});
					if (mention.shouldMention) {
						this.mentionCount++;
					}
				}
				oldestUnread ??= message.id;
			} else {
				loadedOlderMessages = true;
			}
		});
		const hasUnreadBoundary = foundAckMessage || loadedOlderMessages || !messages.hasMoreBefore;
		// "+" only when last-read is outside this window (count is a page-sized lower bound).
		this.estimated = !this.hasAuthoritativeUnreadTotal && !hasUnreadBoundary && messages.length === loadedUnreadCount;
		this.applyRunningRemainingUnread(
			previousRemaining,
			loadedUnreadCount,
			crossedSinceLastCount,
			addedSinceLastCount,
			messages.hasMoreAfter || !messages.hasNewestMessages(),
		);
		if (ackRewound && (messages.hasMoreAfter || !messages.hasNewestMessages()) && this.hasUnread()) {
			this.estimated = true;
		}
		this.oldestUnreadMessageId = this.storedOldestUnreadMessageId ?? oldestUnread;
	}

	private guessAckMessageId(): string | null {
		if (!this.isPrivate) {
			return null;
		}
		const messages = Messages.getMessages(this.channelId);
		if (!messages.hasNewestMessages()) {
			return null;
		}
		if (!this.hasMentions()) {
			return this.storedLastMessageId;
		}
		const currentUserId = Users.getCurrentUser()?.id;
		const candidates: Array<{id: string; authorId: string}> = [];
		messages.forEachBuffered((message) => {
			candidates.push({id: message.id, authorId: message.author.id});
		});
		let remaining = this.mentionCount;
		for (let index = candidates.length - 1; index >= 0; index--) {
			const candidate = candidates[index];
			if (remaining > 0 && candidate.authorId !== currentUserId) {
				remaining--;
			} else if (remaining === 0) {
				return candidate.id;
			}
		}
		return null;
	}

	private resetRemainingUnreadTracking(): void {
		this.storedRemainingUnread = 0;
		this.remainingUnreadInitialized = false;
		this.hasAuthoritativeUnreadTotal = false;
		this.pendingUnreadThroughId = null;
		this.pendingLiveAddsAfterThrough = 0;
		this.countedAckMessageId = null;
		this.countedLastMessageId = null;
	}

	private applyRunningRemainingUnread(
		previousRemaining: number,
		loadedUnreadCount: number,
		crossedSinceLastCount: number,
		addedSinceLastCount: number,
		newerUnreadMayExist: boolean,
	): void {
		const ackAdvanced =
			this.countedAckMessageId != null && compareMessageIds(this.storedAckMessageId, this.countedAckMessageId) > 0;
		const tipAdvanced =
			this.countedLastMessageId != null && compareMessageIds(this.storedLastMessageId, this.countedLastMessageId) > 0;
		let remaining = previousRemaining;
		if (!this.hasAuthoritativeUnreadTotal && !this.remainingUnreadInitialized && previousRemaining === 0) {
			remaining = loadedUnreadCount;
		} else if (ackAdvanced) {
			remaining = Math.max(0, previousRemaining - crossedSinceLastCount);
		} else if (tipAdvanced) {
			remaining = previousRemaining + addedSinceLastCount;
		}
		if (remaining <= 0 && this.hasUnread()) {
			if (loadedUnreadCount > 0) {
				remaining = loadedUnreadCount;
			} else if (newerUnreadMayExist) {
				remaining = 1;
				this.estimated = true;
				this.hasAuthoritativeUnreadTotal = false;
			}
		}
		this.unreadCount = remaining;
		this.storedRemainingUnread = remaining;
		this.remainingUnreadInitialized = this.remainingUnreadInitialized || remaining > 0;
		this.countedAckMessageId = this.storedAckMessageId;
		this.countedLastMessageId = this.storedLastMessageId;
	}

	shouldMentionFor(message: MessageModel | WireMessage, userId: string, isPrivate: boolean): boolean {
		const guildId = this.guildId;
		const member = guildId != null ? GuildMembers.getMember(guildId, userId) : null;
		const signals = resolveMentionSignals({
			...readMentionShape(message),
			currentUserId: userId,
			authorId: message.author.id,
			memberRoleIds: member?.roles ?? null,
			suppressEveryone: UserGuildSettings.isEveryoneMentionSuppressed(guildId) || this.isNonMemberThread(),
			suppressRoles: guildId == null || UserGuildSettings.isRoleMentionSuppressed(guildId),
		});
		return resolveReadStateMention({
			authorBlocked: Relationships.isBlocked(message.author.id),
			hasUserMention: signals.hasUserMention,
			hasEveryoneMention: signals.hasEveryoneMention,
			hasRoleMention: signals.hasRoleMention,
			isPrivate,
			isMuted: UserGuildSettings.isGuildOrChannelMuted(guildId, this.channelId),
		}).shouldMention;
	}

	private isNonMemberThread(): boolean {
		return Channels.getChannel(this.channelId)?.isThread() === true && !ThreadMemberships.isMember(this.channelId);
	}

	computeMentionCountAfterAck(messageId: string): number {
		const currentUser = Users.getCurrentUser();
		if (currentUser == null) {
			return 0;
		}
		const ackTimestamp = snowflakeTimestamp(messageId);
		if (ackTimestamp === 0 || Number.isNaN(ackTimestamp)) {
			return 0;
		}
		const messages = Messages.getMessages(this.channelId);
		const isPrivate = this.isPrivate;
		let mentionCount = 0;
		messages.forEachBuffered((message) => {
			if (snowflakeTimestamp(message.id) <= ackTimestamp) {
				return;
			}
			if (this.shouldMentionFor(message, currentUser.id, isPrivate)) {
				mentionCount++;
			}
		});
		return mentionCount;
	}
}
